import type { Node, Size } from '../types';
import { FONT_FAMILY } from '../constants/typography';
import { measureLabelSize } from './measureLabel';

export type UmlVisibility = '+' | '-' | '#' | '~';
export type UmlOptionality = 'required' | 'optional';

export const UML_VISIBILITY: { value: UmlVisibility; label: string }[] = [
  { value: '+', label: 'public' },
  { value: '-', label: 'private' },
  { value: '#', label: 'protected' },
  { value: '~', label: 'package' },
];

export const UML_FIELD_TYPES = ['string', 'number', 'boolean', 'Date', 'any', 'object', 'void'] as const;

export const UML_TYPE_SHORT: Record<(typeof UML_FIELD_TYPES)[number], string> = {
  string: 'str',
  number: 'int',
  boolean: 'bool',
  Date: 'date',
  any: 'any',
  object: 'obj',
  void: 'void',
};

export function umlTypeShort(type: string): string {
  if (!type) return '—';
  return (UML_TYPE_SHORT as Record<string, string>)[type] ?? type;
}

export function umlTypeFromShort(short: string): string {
  const entry = (Object.entries(UML_TYPE_SHORT) as [string, string][]).find(([, s]) => s === short);
  return entry?.[0] ?? short;
}

export interface UmlAttribute {
  visibility: UmlVisibility;
  name: string;
  type: string;
  optionality: UmlOptionality;
}

export interface UmlMethod {
  visibility: UmlVisibility;
  name: string;
  params: string;
  returnType: string;
}

const ATTR_FONT = `10px ${FONT_FAMILY}`;
const TITLE_FONT = `600 13px ${FONT_FAMILY}`;
const LINE_H = 15;
const TITLE_H = 18;
const SECTION_PAD = 10;
const MIN_WIDTH = 168;

export function parseAttribute(line: string): UmlAttribute {
  const trimmed = line.trim();
  const visMatch = trimmed.match(/^([+\-#~])\s*/);
  const visibility = (visMatch?.[1] as UmlVisibility) ?? '+';
  let rest = visMatch ? trimmed.slice(visMatch[0].length) : trimmed;

  let optionality: UmlOptionality = 'required';
  if (/\?:/.test(rest) || /\?\s*$/.test(rest) || /\?\s*:/.test(rest)) {
    optionality = 'optional';
    rest = rest.replace('?', '');
  }

  const colon = rest.indexOf(':');
  if (colon >= 0) {
    return {
      visibility,
      name: rest.slice(0, colon).trim() || 'field',
      type: rest.slice(colon + 1).trim(),
      optionality,
    };
  }
  return {
    visibility,
    name: rest.trim() || 'field',
    type: '',
    optionality,
  };
}

export function parseMethod(line: string): UmlMethod {
  const trimmed = line.trim();
  const visMatch = trimmed.match(/^([+\-#~])\s*/);
  const visibility = (visMatch?.[1] as UmlVisibility) ?? '+';
  const rest = visMatch ? trimmed.slice(visMatch[0].length) : trimmed;

  const paren = rest.indexOf('(');
  if (paren < 0) {
    const colon = rest.indexOf(':');
    return {
      visibility,
      name: (colon >= 0 ? rest.slice(0, colon) : rest).trim() || 'method',
      params: '',
      returnType: colon >= 0 ? rest.slice(colon + 1).trim() : 'void',
    };
  }
  const name = rest.slice(0, paren).trim() || 'method';
  const close = rest.indexOf(')', paren);
  const params = close >= 0 ? rest.slice(paren + 1, close).trim() : '';
  const after = close >= 0 ? rest.slice(close + 1) : '';
  const colon = after.indexOf(':');
  const returnType = colon >= 0 ? after.slice(colon + 1).trim() : 'void';
  return { visibility, name, params, returnType };
}

export function formatAttribute(attr: UmlAttribute): string {
  const opt = attr.optionality === 'optional' ? '?' : '';
  if (!attr.type) return `${attr.visibility} ${attr.name}${opt}`.trim();
  return `${attr.visibility} ${attr.name}${opt}: ${attr.type}`;
}

export function formatMethod(method: UmlMethod): string {
  const ret = method.returnType ? `: ${method.returnType}` : '';
  return `${method.visibility} ${method.name}(${method.params})${ret}`;
}

export function classifierLines(node: Pick<Node, 'label' | 'data'>): { stereotype: string; attributes: string[]; methods: string[] } {
  const stereotype = typeof node.data?.stereotype === 'string' ? node.data.stereotype : '';
  const attributes = Array.isArray(node.data?.attributes) ? node.data.attributes as string[] : [];
  const methods = Array.isArray(node.data?.methods) ? node.data.methods as string[] : [];
  return { stereotype, attributes, methods };
}

/** Minimum box that keeps every compartment inside the classifier outline. */
export function classifierBoxSize(node: Pick<Node, 'type' | 'label' | 'data'>): Size {
  const { stereotype, attributes, methods } = classifierLines(node);
  const headerH = (stereotype ? 14 : 0) + TITLE_H + 10;
  const attrH = Math.max(20, attributes.length * LINE_H + SECTION_PAD);
  const showMethods = node.type !== 'uml_enum' || methods.length > 0;
  const methH = showMethods ? Math.max(20, methods.length * LINE_H + SECTION_PAD) : 0;

  let width = MIN_WIDTH;
  const bump = (text: string, font: string) => {
    width = Math.max(width, Math.ceil(measureLabelSize(text, font, LINE_H).width) + 28);
  };
  bump(node.label || 'Class', TITLE_FONT);
  if (stereotype) bump(`«${stereotype}»`, `9px ${FONT_FAMILY}`);
  for (const line of attributes) bump(line || ' ', ATTR_FONT);
  for (const line of methods) bump(line || ' ', ATTR_FONT);

  return {
    width,
    height: headerH + attrH + methH + 6,
  };
}
